import { RefreshCwIcon } from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import { usePrimaryEnvironmentDescriptor } from "../../environments/primary";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { useEnvironmentStoreSnapshot } from "../../hooks/useEnvironmentStoreSnapshot";
import {
  ensureClientSettingsHydrated,
  useClientSettingsHydrationStatus,
  useSettings,
} from "../../hooks/useSettings";
import { shouldUseCmmdFirstAccountSetupState } from "../../lib/firstAccountSetup";
import { useCompleteOnboarding } from "../../onboarding/firstRun";
import {
  isFirstRunWorkspaceProvenanceAuthoritative,
  isFreshFirstRunWorkspace,
  resolveFirstRunDecision,
  transitionFirstRunGateState,
  type FirstRunDecision,
  type FirstRunGateState,
} from "../../onboarding/firstRun.logic";
import { useServerConfig, useServerWelcome } from "../../rpc/serverState";
import {
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
} from "../../store";
import { Button } from "../ui/button";
import { applyCloudEnvironmentGate, classifyCloudEnvironmentCatalog } from "./FirstRunGate.logic";

/**
 * Holds back the authenticated app until the first-run decision is known, so a
 * fresh browser never flashes the workspace before the welcome wizard.
 * Ported from upstream's `components/onboarding/FirstRunGate.tsx` onto forge's
 * state: the primary environment's config and welcome payload are the
 * workspace evidence, and the saved-environment registry is the catalog.
 *
 * Decision order is upstream's: a set onboardingCompletedAt resolves to the
 * app as soon as client settings hydrate (the common case, no server
 * round-trip). Otherwise the gate waits for the welcome payload and inspects
 * the workspace. A timeout shows recovery for an unreachable workspace
 * without mounting the app tree.
 */

const FIRST_RUN_DECISION_TIMEOUT_MS = 4_000;

function useFirstRunGateDecision(input: {
  readonly enabled: boolean;
  readonly hydrated: boolean;
  readonly completed: boolean;
  readonly nextDecision: FirstRunDecision;
  readonly persistCompletion: boolean;
  readonly pathname: string;
}): FirstRunGateState {
  const navigate = useNavigate();
  const completeOnboarding = useCompleteOnboarding();
  const onboardingCompletedAt = useSettings((settings) => settings.onboardingCompletedAt);
  const [gateState, setGateState] = useState<FirstRunGateState>(() => ({
    decision: !input.enabled || (input.hydrated && input.completed) ? "app" : "pending",
    stalled: false,
  }));

  useEffect(() => {
    if (gateState.decision === "wizard" || !input.hydrated) return;

    if (input.persistCompletion && onboardingCompletedAt === null) {
      void completeOnboarding().catch(() => undefined);
    }

    setGateState((state) =>
      transitionFirstRunGateState(state, { type: "evidence", decision: input.nextDecision }),
    );
  }, [
    completeOnboarding,
    gateState.decision,
    input.completed,
    input.enabled,
    input.hydrated,
    input.nextDecision,
    input.persistCompletion,
    onboardingCompletedAt,
  ]);

  // A stalled workspace read gets a recovery screen, but never mounts the app.
  // The timer starts after settings hydrate so slow hydration does not show a
  // false connection failure.
  useEffect(() => {
    if (!input.enabled || gateState.decision !== "pending" || !input.hydrated) return;
    const timer = window.setTimeout(
      () => setGateState((state) => transitionFirstRunGateState(state, { type: "timeout" })),
      FIRST_RUN_DECISION_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [gateState.decision, input.enabled, input.hydrated]);

  useEffect(() => {
    if (gateState.decision === "wizard" && input.pathname !== "/welcome") {
      void navigate({ to: "/welcome", replace: true });
    }
  }, [gateState.decision, input.pathname, navigate]);

  return gateState;
}

interface WorkspaceEvidence {
  readonly catalogReady: boolean;
  readonly projectCount: number;
  readonly threadCount: number;
  readonly workspaceFresh: boolean;
}

/**
 * Projects and threads across every environment, reduced to the shapes the
 * first-run logic reads, plus the freshness verdict over them.
 */
function useWorkspaceEvidence(
  serverConfig: ReturnType<typeof useServerConfig>,
  welcome: ReturnType<typeof useServerWelcome>,
): WorkspaceEvidence {
  const catalogReady = useSavedEnvironmentRegistryStore((store) => store.hydrated);
  const storeSnapshot = useEnvironmentStoreSnapshot();
  const projects = useMemo(
    () =>
      selectProjectsAcrossEnvironments(storeSnapshot).map((project) => ({
        id: project.id,
        environmentId: project.environmentId,
        workspaceRoot: project.cwd,
      })),
    [storeSnapshot],
  );
  const threads = useMemo(
    () =>
      selectSidebarThreadsAcrossEnvironments(storeSnapshot).map((thread) => ({
        id: thread.id,
        projectId: thread.projectId,
        environmentId: thread.environmentId,
        latestTurn: thread.latestTurn,
        latestUserMessageAt: thread.latestUserMessageAt,
        session: thread.session,
      })),
    [storeSnapshot],
  );

  const workspaceFresh = isFreshFirstRunWorkspace({
    primaryEnvironmentId: serverConfig?.environment.environmentId ?? null,
    serverCwd: serverConfig?.cwd ?? null,
    bootstrapProjectId: welcome?.bootstrapProjectId,
    bootstrapThreadId: welcome?.bootstrapThreadId,
    // Forge's welcome payload carries the bootstrap ids but no created flags:
    // the server reports the same ids whether it created or reused the cwd
    // project. Ids present is the closest forge signal for "created by this
    // startup", so a reused single workspace opens the wizard once; finishing
    // or skipping it writes the flag and it never reopens.
    bootstrapProjectCreated: welcome?.bootstrapProjectId !== undefined,
    bootstrapThreadCreated: welcome?.bootstrapThreadId !== undefined,
    projects,
    threads,
  });

  return {
    catalogReady,
    projectCount: projects.length,
    threadCount: threads.length,
    workspaceFresh,
  };
}

export function FirstRunGate({
  enabled,
  children,
}: {
  readonly enabled: boolean;
  readonly children: React.ReactNode;
}) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const hydrationStatus = useClientSettingsHydrationStatus();
  const hydrated = hydrationStatus === "ready";
  const onboardingCompletedAt = useSettings((settings) => settings.onboardingCompletedAt);
  const serverConfig = useServerConfig();
  const welcome = useServerWelcome();
  const { catalogReady, projectCount, threadCount, workspaceFresh } = useWorkspaceEvidence(
    serverConfig,
    welcome,
  );

  const authoritative = serverConfig !== null;

  // CMMD's own cloud catalog knows whether this account already has an
  // Environment, independent of whether the client has connected to it. A
  // suspended Environment reports zero live projects and threads until it
  // wakes, which must never look like a workspace nobody has set up yet
  // (CMMD-Center/forge#3626). See FirstRunGate.logic.ts.
  const primaryEnvironmentDescriptor = usePrimaryEnvironmentDescriptor();
  const cloudEnvironmentGateApplies = primaryEnvironmentDescriptor
    ? shouldUseCmmdFirstAccountSetupState(primaryEnvironmentDescriptor)
    : false;
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot({ enabled: cloudEnvironmentGateApplies });
  const cloudEnvironmentCatalogState = classifyCloudEnvironmentCatalog({
    isLoading: cloudCatalog.isLoading,
    fetchedAt: cloudCatalog.fetchedAt,
    environmentCount: cloudCatalog.environments.length,
  });

  const rawDecision = resolveFirstRunDecision({
    enabled,
    hydrated,
    completed: onboardingCompletedAt !== null,
    bootstrapped: authoritative,
    authoritative,
    workspaceAuthoritative: authoritative,
    workspaceProvenanceAuthoritative: isFirstRunWorkspaceProvenanceAuthoritative({
      welcomeReceived: welcome !== null,
      // Forge's welcome payload has no bootstrap status; a received welcome is
      // final evidence, the same shape as upstream's legacy servers.
      bootstrapStatus: null,
    }),
    catalogReady,
    serverConfigAvailable: serverConfig !== null,
    workspaceFresh,
    projectCount,
    threadCount,
  });
  const { decision: nextDecision, persistCompletion } = applyCloudEnvironmentGate({
    nextDecision: rawDecision.decision,
    persistCompletion: rawDecision.persistCompletion,
    cloudEnvironmentGateApplies,
    cloudEnvironmentCatalogState,
  });

  // Within a session settings stay hydrated, so remounts (e.g. returning from
  // the wizard) resolve synchronously instead of blanking a frame.
  const { decision, stalled } = useFirstRunGateDecision({
    enabled,
    hydrated,
    completed: onboardingCompletedAt !== null,
    nextDecision,
    persistCompletion,
    pathname,
  });

  if (hydrationStatus === "failed") {
    return <FirstRunRecovery reason="settings" />;
  }
  if (decision !== "app") {
    return stalled ? <FirstRunRecovery reason="connection" /> : null;
  }
  return children;
}

function FirstRunRecovery({ reason }: { readonly reason: "settings" | "connection" }) {
  const settingsReadFailed = reason === "settings";
  return (
    <main className="flex h-dvh min-h-0 items-center justify-center bg-background px-6 text-foreground">
      <div className="flex max-w-sm flex-col items-center text-center">
        <h1 className="text-lg font-semibold">
          {settingsReadFailed ? "Could not read settings" : "Still connecting"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {settingsReadFailed
            ? "Your saved settings could not be loaded."
            : "Forge could not confirm this workspace."}
        </p>
        <Button
          className="mt-5"
          size="sm"
          variant="outline"
          onClick={() => {
            if (settingsReadFailed) {
              void ensureClientSettingsHydrated().catch(() => undefined);
            } else {
              window.location.reload();
            }
          }}
        >
          <RefreshCwIcon className="size-4" />
          {settingsReadFailed ? "Retry" : "Reload"}
        </Button>
      </div>
    </main>
  );
}
